import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import {
  AUTH_TYPE_LABELS,
  CARRIER_ACTIONS,
  CONNECTOR_AUTH_TYPES,
  PORTOUT_NETWORKS,
  customConnectorSchema,
} from '@app/shared';
import type {
  CarrierActionKey,
  ConnectorAuthType,
  ConnectorTryDto,
  CustomConnectorDto,
  CustomConnectorInput,
} from '@app/shared';
import { ConfirmDialog, FormDialog } from '../../components/dialogs';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { ApiError, api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';

/**
 * Settings > Connections > Carrier connectors: a carrier's API set up on the page, without code.
 * A connector made here is offered in the MVNE select of every carrier below.
 */

export const CONNECTORS_KEY = ['connections', 'connectors'];
const CARRIERS_KEY = ['connections', 'carriers'];

type Template = NonNullable<CustomConnectorInput['actions']>[CarrierActionKey];
type Draft = CustomConnectorInput & {
  auth: NonNullable<CustomConnectorInput['auth']>;
  fields: NonNullable<CustomConnectorInput['fields']>;
};

const blank = (): Draft => ({
  label: '',
  baseUrl: 'https://',
  sandboxUrl: '',
  where: '',
  auth: { type: 'bearer', keyField: 'apiKey' },
  fields: [{ key: 'apiKey', label: 'API key', secret: true, required: true }],
  headers: {},
  actions: {},
  testInput: {},
});

const blankTemplate = (): NonNullable<Template> => ({
  method: 'POST',
  path: '/',
  bodyType: 'json',
  body: '',
});

/** The definition alone, as exported and imported (no id, owner or dates). */
const definitionOf = (c: CustomConnectorDto): CustomConnectorInput => {
  const { id: _id, key: _key, shared: _s, own: _o, updatedAt: _u, updatedBy: _b, ...def } = c;
  return def;
};

/** `name: value` lines, one per line, for headers, token fields and test values. */
const linesOf = (r: Record<string, string> | undefined) =>
  Object.entries(r ?? {})
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');
const recordOf = (text: string): Record<string, string> =>
  Object.fromEntries(
    text
      .split('\n')
      .map((l) => /^\s*([^:]+?)\s*:\s?(.*)$/.exec(l))
      .filter((m): m is RegExpExecArray => !!m)
      .map((m) => [m[1]!, m[2]!.trim()]),
  );

const readText = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Not readable'));
    reader.readAsText(f);
  });

function saveFile(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const problemsOf = (issues: Array<{ path: string; message: string }>) =>
  Object.fromEntries(issues.map((i) => [i.path, i.message]));

/* ---------------------------------- Editor ---------------------------------- */

function ConnectorDialog({
  start,
  editing,
  onClose,
}: {
  start: Draft;
  editing: CustomConnectorDto | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [d, setD] = useState<Draft>(start);
  const [headers, setHeaders] = useState(linesOf(start.headers));
  const [tokenParams, setTokenParams] = useState(linesOf(start.auth.tokenParams));
  const [testInput, setTestInput] = useState(linesOf(start.testInput));
  const [problems, setProblems] = useState<Record<string, string>>({});
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const setAuth = (patch: Partial<Draft['auth']>) =>
    setD((x) => ({ ...x, auth: { ...x.auth, ...patch } }));
  const setCallbacks = (patch: Partial<NonNullable<Draft['callbacks']>>) =>
    setD((x) => ({ ...x, callbacks: { ...x.callbacks, ...patch } }));
  const setField = (i: number, patch: Partial<Draft['fields'][number]>) =>
    setD((x) => ({ ...x, fields: x.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) }));
  const setAction = (key: CarrierActionKey, t: Template | undefined) =>
    setD((x) => {
      const actions = { ...x.actions };
      if (t) actions[key] = t;
      else delete actions[key];
      return { ...x, actions };
    });
  const save = useMutation({
    mutationFn: (body: CustomConnectorInput) =>
      editing
        ? api.put<CustomConnectorDto>(`/connections/connectors/${editing.id}`, body)
        : api.post<CustomConnectorDto>('/connections/connectors', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CONNECTORS_KEY });
      void queryClient.invalidateQueries({ queryKey: CARRIERS_KEY });
    },
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body: CustomConnectorInput = {
      ...d,
      headers: recordOf(headers),
      auth: { ...d.auth, tokenParams: recordOf(tokenParams) },
      testInput: recordOf(testInput),
    };
    const checked = customConnectorSchema.safeParse(body);
    if (!checked.success) {
      setProblems(
        problemsOf(
          checked.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })),
        ),
      );
      return;
    }
    try {
      const saved = await save.mutateAsync(body);
      notify.success(
        `${saved.label} saved. Pick it as the MVNE of a carrier below, enter the values and press Test connection.`,
      );
      onClose();
    } catch (err) {
      const found = err instanceof ApiError ? problemsOf(err.details) : {};
      setProblems(found);
      if (Object.keys(found).length === 0) notify.error(err);
    }
  };

  const err = (path: string) => ({ error: !!problems[path], helperText: problems[path] });
  const keys = d.fields.map((f) => f.key).filter(Boolean);
  const keySelect = (label: string, name: 'userField' | 'passwordField' | 'keyField') => (
    <TextField
      select
      required
      label={label}
      value={d.auth[name] ?? ''}
      onChange={(e) => setAuth({ [name]: e.target.value })}
      {...err(`auth.${name}`)}
    >
      {keys.map((k) => (
        <MenuItem key={k} value={k}>
          {k}
        </MenuItem>
      ))}
    </TextField>
  );
  const type = d.auth.type;
  const listed = Object.entries(problems);

  return (
    <FormDialog
      open
      title={editing ? `Edit carrier connector ${editing.label}` : 'New carrier connector'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      submitLabel="Save"
      maxWidth="lg"
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        {listed.length ? (
          <Alert severity="error">
            {listed.length} field(s) need attention:{' '}
            {listed.map(([p, m]) => `${p}: ${m}`).join('; ')}
          </Alert>
        ) : null}
        <Typography variant="h3">The carrier&apos;s API</Typography>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
          <TextField
            required
            label="Name"
            value={d.label}
            onChange={(e) => set({ label: e.target.value })}
            {...err('label')}
          />
          <TextField
            label="Where an MVNO finds the values"
            value={d.where ?? ''}
            onChange={(e) => set({ where: e.target.value })}
            {...err('where')}
          />
          <TextField
            required
            label="Live base address"
            value={d.baseUrl}
            onChange={(e) => set({ baseUrl: e.target.value })}
            {...err('baseUrl')}
          />
          <TextField
            label="Sandbox base address"
            value={d.sandboxUrl ?? ''}
            onChange={(e) => set({ sandboxUrl: e.target.value })}
            error={!!problems.sandboxUrl}
            helperText={
              problems.sandboxUrl ?? 'Used by connections in Sandbox mode. Empty: the live address.'
            }
          />
        </Box>

        <Typography variant="h3">Values each MVNO enters</Typography>
        <Typography variant="body2" color="text.secondary">
          Its own account with the carrier. A secret value is stored encrypted and never shown
          again. Use a value in a request as {'{{values.key}}'}.
        </Typography>
        {d.fields.map((f, i) => (
          <Box
            key={i}
            role="group"
            aria-label={`Value ${i + 1}`}
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: { md: '1fr 1.4fr auto auto 1.6fr auto' },
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              required
              label="Key"
              value={f.key}
              onChange={(e) => setField(i, { key: e.target.value })}
              {...err(`fields.${i}.key`)}
            />
            <TextField
              size="small"
              required
              label="Label"
              value={f.label}
              onChange={(e) => setField(i, { label: e.target.value })}
              {...err(`fields.${i}.label`)}
            />
            <FormControlLabel
              label="Secret"
              control={
                <Checkbox
                  checked={!!f.secret}
                  onChange={(e) => setField(i, { secret: e.target.checked })}
                />
              }
            />
            <FormControlLabel
              label="Required"
              control={
                <Checkbox
                  checked={f.required !== false}
                  onChange={(e) => setField(i, { required: e.target.checked })}
                />
              }
            />
            <TextField
              size="small"
              label="Help"
              value={f.help ?? ''}
              onChange={(e) => setField(i, { help: e.target.value })}
            />
            <IconButton
              aria-label={`Remove value ${i + 1}`}
              onClick={() => set({ fields: d.fields.filter((_, j) => j !== i) })}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        {problems.fields ? <Alert severity="error">{problems.fields}</Alert> : null}
        <Box>
          <Button
            size="small"
            onClick={() =>
              set({ fields: [...d.fields, { key: '', label: '', secret: false, required: true }] })
            }
          >
            Add a value
          </Button>
        </Box>

        <Typography variant="h3">Sign-in</Typography>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr 1fr' } }}>
          <TextField
            select
            required
            label="Sign-in type"
            value={type}
            onChange={(e) => setAuth({ type: e.target.value as ConnectorAuthType })}
          >
            {CONNECTOR_AUTH_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {AUTH_TYPE_LABELS[t]}
              </MenuItem>
            ))}
          </TextField>
          {type === 'apiKeyHeader' ? (
            <>
              <TextField
                required
                label="Header"
                placeholder="X-Api-Key"
                value={d.auth.headerName ?? ''}
                onChange={(e) => setAuth({ headerName: e.target.value })}
                {...err('auth.headerName')}
              />
              {keySelect('Key value', 'keyField')}
            </>
          ) : null}
          {type === 'bearer' ? keySelect('Token value', 'keyField') : null}
          {type === 'basic' ? (
            <>
              {keySelect('User value', 'userField')}
              {keySelect('Password value', 'passwordField')}
            </>
          ) : null}
          {type === 'oauth2Password' || type === 'oauth2ClientCredentials' ? (
            <>
              <TextField
                required
                label="Token address"
                value={d.auth.tokenUrl ?? ''}
                onChange={(e) => setAuth({ tokenUrl: e.target.value })}
                {...err('auth.tokenUrl')}
              />
              {keySelect(type === 'oauth2Password' ? 'User value' : 'Client ID value', 'userField')}
              {keySelect(
                type === 'oauth2Password' ? 'Password value' : 'Client secret value',
                'passwordField',
              )}
              <TextField
                multiline
                minRows={2}
                label="Other token fields (name: value)"
                value={tokenParams}
                onChange={(e) => setTokenParams(e.target.value)}
                helperText="For example client_id: {{values.clientId}}"
              />
            </>
          ) : null}
        </Box>
        <TextField
          multiline
          minRows={2}
          label="Headers on every request (name: value)"
          value={headers}
          onChange={(e) => setHeaders(e.target.value)}
          helperText="For example X-Reseller-Id: {{values.resellerId}}"
        />

        <Typography variant="h3">Requests</Typography>
        <Typography variant="body2" color="text.secondary">
          One request per action the carrier offers. An action without a request is refused with a
          message. Paths of the answer: <code>data.phoneNumber</code>, <code>lines[0].id</code>, or{' '}
          <code>xml:mdn</code> for the first &lt;mdn&gt; of an XML answer.
        </Typography>
        <Box>
          {CARRIER_ACTIONS.map((a) => {
            const t = d.actions?.[a.key];
            const p = (name: string) => err(`actions.${a.key}.${name}`);
            const setT = (patch: Partial<NonNullable<Template>>) =>
              setAction(a.key, { ...(t ?? blankTemplate()), ...patch });
            return (
              <Accordion key={a.key} disableGutters defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ flex: 1 }}>{a.label}</Typography>
                  {t ? <Chip size="small" color="success" label={`${t.method} ${t.path}`} /> : null}
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'grid', gap: 1.5 }}>
                  <FormControlLabel
                    label={`This carrier offers "${a.label}"`}
                    control={
                      <Checkbox
                        checked={!!t}
                        onChange={(e) =>
                          setAction(a.key, e.target.checked ? blankTemplate() : undefined)
                        }
                      />
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    Placeholders: {a.vars.map((v) => `{{${v}}}`).join(' ')}
                    {keys.length ? ` ${keys.map((k) => `{{values.${k}}}`).join(' ')}` : ''}
                  </Typography>
                  {t ? (
                    <>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 1.5,
                          gridTemplateColumns: { md: '0.6fr 2fr 0.8fr' },
                        }}
                      >
                        <TextField
                          select
                          size="small"
                          label="Method"
                          value={t.method}
                          onChange={(e) =>
                            setT({ method: e.target.value as NonNullable<Template>['method'] })
                          }
                        >
                          {(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const).map((m) => (
                            <MenuItem key={m} value={m}>
                              {m}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="small"
                          required
                          label="Path"
                          value={t.path}
                          onChange={(e) => setT({ path: e.target.value })}
                          {...p('path')}
                        />
                        <TextField
                          select
                          size="small"
                          label="Body"
                          value={t.bodyType ?? 'json'}
                          onChange={(e) =>
                            setT({ bodyType: e.target.value as NonNullable<Template>['bodyType'] })
                          }
                        >
                          {[
                            ['none', 'None'],
                            ['json', 'JSON'],
                            ['form', 'Form'],
                            ['xml', 'XML'],
                          ].map(([v, l]) => (
                            <MenuItem key={v} value={v}>
                              {l}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                      {t.bodyType !== 'none' && t.method !== 'GET' ? (
                        <TextField
                          multiline
                          minRows={3}
                          label="Request body"
                          value={t.body ?? ''}
                          onChange={(e) => setT({ body: e.target.value })}
                          slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
                          {...p('body')}
                        />
                      ) : null}
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 1.5,
                          gridTemplateColumns: { md: 'repeat(3, 1fr)' },
                        }}
                      >
                        <TextField
                          size="small"
                          label="Success when this path"
                          value={t.successPath ?? ''}
                          onChange={(e) => setT({ successPath: e.target.value })}
                          helperText="Empty: any 2xx answer is a success"
                        />
                        <TextField
                          size="small"
                          label="equals"
                          value={t.successEquals ?? ''}
                          onChange={(e) => setT({ successEquals: e.target.value })}
                        />
                        <TextField
                          size="small"
                          label="Error message path"
                          value={t.errorPath ?? ''}
                          onChange={(e) => setT({ errorPath: e.target.value })}
                        />
                        <TextField
                          size="small"
                          label="Phone number path"
                          value={t.mdnPath ?? ''}
                          onChange={(e) => setT({ mdnPath: e.target.value })}
                        />
                        <TextField
                          size="small"
                          label="Carrier reference path"
                          value={t.referencePath ?? ''}
                          onChange={(e) => setT({ referencePath: e.target.value })}
                        />
                        <TextField
                          size="small"
                          label="Status path"
                          value={t.statusPath ?? ''}
                          onChange={(e) => setT({ statusPath: e.target.value })}
                          helperText={
                            a.key === 'queryPortIn'
                              ? 'COMPLETE/PORTED: Completed; FAIL/REJECT/CANCEL: Failed; else Pending'
                              : undefined
                          }
                        />
                      </Box>
                    </>
                  ) : null}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

        <Typography variant="h3">Callbacks from the carrier</Typography>
        <Typography variant="body2" color="text.secondary">
          If the carrier posts events to 212Ez (each carrier card shows its callback address), say
          where the event name and the phone number are in what it posts, and which event names mean
          what. Leave it empty if the carrier sends nothing: port-ins are then checked every 15
          minutes with the Port-in status request.
        </Typography>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: 'repeat(3, 1fr)' } }}>
          <TextField
            size="small"
            label="Event name path"
            value={d.callbacks?.eventPath ?? ''}
            onChange={(e) => setCallbacks({ eventPath: e.target.value })}
            error={!!problems['callbacks.eventPath']}
            helperText={
              problems['callbacks.eventPath'] ?? 'For example event.type or xml:eventType'
            }
          />
          <TextField
            size="small"
            label="Phone number path"
            value={d.callbacks?.mdnPath ?? ''}
            onChange={(e) => setCallbacks({ mdnPath: e.target.value })}
            error={!!problems['callbacks.mdnPath']}
            helperText={problems['callbacks.mdnPath']}
          />
          <TextField
            size="small"
            label="Gaining carrier path (port-out)"
            value={d.callbacks?.gainingCarrierPath ?? ''}
            onChange={(e) => setCallbacks({ gainingCarrierPath: e.target.value })}
          />
          <TextField
            size="small"
            label="Events that mean a port-in changed"
            value={d.callbacks?.portInEvents ?? ''}
            onChange={(e) => setCallbacks({ portInEvents: e.target.value })}
            helperText="Comma separated; 212Ez then asks the carrier for the port's status"
          />
          <TextField
            size="small"
            label="Events that mean a port-out request"
            value={d.callbacks?.portOutEvents ?? ''}
            onChange={(e) => setCallbacks({ portOutEvents: e.target.value })}
            helperText="Comma separated"
          />
          <TextField
            select
            size="small"
            label="Port-outs go to"
            value={d.callbacks?.portOutNetwork ?? PORTOUT_NETWORKS[0]}
            onChange={(e) =>
              setCallbacks({ portOutNetwork: e.target.value as (typeof PORTOUT_NETWORKS)[number] })
            }
            helperText="T-Mobile: the number goes at once. The others: a Pending Port-out list to answer"
          >
            {PORTOUT_NETWORKS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Typography variant="h3">Test connection</Typography>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 2fr' } }}>
          <TextField
            select
            label="Request Test connection runs"
            value={d.testAction ?? ''}
            onChange={(e) =>
              set({ testAction: (e.target.value || undefined) as CarrierActionKey | undefined })
            }
            error={!!problems.testAction}
            helperText={problems.testAction ?? 'Empty: Test connection only signs in.'}
          >
            <MenuItem value="">(sign in only)</MenuItem>
            {CARRIER_ACTIONS.filter((a) => d.actions?.[a.key]).map((a) => (
              <MenuItem key={a.key} value={a.key}>
                {a.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            multiline
            minRows={2}
            label="Its sample input (name: value)"
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            helperText="For example mdn: 5550001111 (a test line of your carrier account)"
          />
        </Box>
      </Box>
    </FormDialog>
  );
}

/* ------------------------------- Try a request ------------------------------- */

export function TryRequestDialog({
  carrierId,
  carrierCode,
  connectorKey,
  onClose,
}: {
  carrierId: string;
  carrierCode: string;
  connectorKey: string;
  onClose: () => void;
}) {
  const notify = useNotify();
  const defs = useQuery({
    queryKey: CONNECTORS_KEY,
    queryFn: () => api.get<{ data: CustomConnectorDto[] }>('/connections/connectors'),
    select: (r) => r.data,
  });
  const def = defs.data?.find((c) => c.key === connectorKey);
  const offered = CARRIER_ACTIONS.filter((a) => def?.actions[a.key]);
  const [action, setAction] = useState<CarrierActionKey | ''>('');
  const [input, setInput] = useState<Record<string, string>>({});
  const run = useMutation({
    mutationFn: () =>
      api.post<ConnectorTryDto>(`/connections/carriers/${carrierId}/try`, { action, input }),
  });
  const vars = CARRIER_ACTIONS.find((a) => a.key === action)?.vars ?? [];
  const r = run.data;
  return (
    <FormDialog
      open
      title={`Try a request: carrier ${carrierCode}`}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        if (action) run.mutate(undefined, { onError: (err) => notify.error(err) });
      }}
      submitting={run.isPending}
      submitLabel="Send"
      cancelLabel="Close"
      maxWidth="md"
    >
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Alert severity="warning">
          This is a real call to the carrier with this carrier&apos;s connection (Sandbox or Live as
          set). It is kept in the API log. Use a test line.
        </Alert>
        {defs.isError ? (
          <ErrorState error={defs.error} onRetry={() => void defs.refetch()} />
        ) : null}
        <TextField
          select
          required
          label="Action"
          value={action}
          onChange={(e) => {
            setAction(e.target.value as CarrierActionKey);
            setInput({});
            run.reset();
          }}
        >
          {offered.map((a) => (
            <MenuItem key={a.key} value={a.key}>
              {a.label}
            </MenuItem>
          ))}
        </TextField>
        {action ? (
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr 1fr' } }}>
            {vars.map((v) => (
              <TextField
                key={v}
                size="small"
                label={v}
                autoComplete="off"
                type={v === 'pin' ? 'password' : 'text'}
                value={input[v] ?? ''}
                onChange={(e) => setInput((x) => ({ ...x, [v]: e.target.value }))}
              />
            ))}
          </Box>
        ) : null}
        {r ? (
          <Paper variant="outlined" sx={{ p: 1.5, display: 'grid', gap: 1 }} aria-label="Answer">
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                size="small"
                color={r.ok ? 'success' : 'error'}
                label={r.ok ? 'Success' : 'Failed'}
              />
              <Typography variant="body2">
                HTTP {r.httpStatus ?? '-'} · {r.durationMs} ms
              </Typography>
            </Box>
            {r.request.url ? (
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <b>{r.request.method}</b> {r.request.url}
              </Typography>
            ) : null}
            {r.request.body ? (
              <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                {r.request.body}
              </Box>
            ) : null}
            <Typography variant="body2">
              Read: reference {r.read.reference || '-'}, number {r.read.mdn || '-'}, status{' '}
              {r.read.status || '-'}
              {r.read.error ? `, error: ${r.read.error}` : ''}
            </Typography>
            {r.answer ? (
              <Box
                component="pre"
                aria-label="Answer body"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  fontSize: 12,
                  maxHeight: 260,
                  overflow: 'auto',
                }}
              >
                {r.answer}
              </Box>
            ) : null}
          </Paper>
        ) : null}
      </Box>
    </FormDialog>
  );
}

/* ----------------------------------- List ----------------------------------- */

export function CarrierConnectorsSection() {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const [editor, setEditor] = useState<{ start: Draft; editing: CustomConnectorDto | null } | null>(
    null,
  );
  const [removing, setRemoving] = useState<CustomConnectorDto | null>(null);
  const list = useQuery({
    queryKey: CONNECTORS_KEY,
    queryFn: () => api.get<{ data: CustomConnectorDto[] }>('/connections/connectors'),
    select: (r) => r.data,
  });
  const remove = useMutation({
    mutationFn: (c: CustomConnectorDto) => api.delete(`/connections/connectors/${c.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CONNECTORS_KEY });
      void queryClient.invalidateQueries({ queryKey: CARRIERS_KEY });
    },
  });

  const importFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    e.target.value = '';
    if (!chosen) return;
    try {
      const raw = JSON.parse(await readText(chosen)) as Record<string, unknown>;
      const parsed = customConnectorSchema.safeParse(raw);
      const start = { ...blank(), ...(parsed.success ? parsed.data : raw) } as Draft;
      setEditor({ start, editing: null });
      notify.success(`${chosen.name} read. Check it and press Save.`);
    } catch {
      notify.error(new Error(`${chosen.name} is not a connector file (JSON).`));
    }
  };

  return (
    <Box sx={{ mt: 3 }} component="section" aria-label="Carrier connectors">
      <Typography variant="h2" sx={{ mb: 1 }}>
        Carrier connectors
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        A carrier that 212Ez has no built-in connector for is set up here from its API documents:
        the address, how to sign in, the values each MVNO enters and one request per action. It is
        then offered in the MVNE select of each carrier above, with no code release. Export one to
        use it on another 212Ez, or import a file someone gave you.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <Button variant="contained" onClick={() => setEditor({ start: blank(), editing: null })}>
          New connector
        </Button>
        <Button variant="outlined" onClick={() => file.current?.click()}>
          Import JSON
        </Button>
        <input
          ref={file}
          type="file"
          accept="application/json,.json"
          hidden
          aria-label="Connector file"
          onChange={(e) => void importFile(e)}
        />
      </Box>
      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <LoadingState />
      ) : list.data.length === 0 ? (
        <Alert severity="info">No carrier connector made here yet.</Alert>
      ) : (
        <Box sx={{ display: 'grid', gap: 1 }}>
          {list.data.map((c) => (
            <Paper
              key={c.id}
              variant="outlined"
              component="section"
              aria-label={`Connector ${c.label}`}
              sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Typography variant="h3">
                  {c.label} {c.shared ? <Chip size="small" label="Shared by the platform" /> : null}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {c.baseUrl} · {AUTH_TYPE_LABELS[c.auth.type]} · {Object.keys(c.actions).length} of{' '}
                  {CARRIER_ACTIONS.length} actions · changed {formatDateTime(c.updatedAt)}
                  {c.updatedBy ? ` by ${c.updatedBy}` : ''}
                </Typography>
              </Box>
              {c.own ? (
                <Button
                  onClick={() =>
                    setEditor({ start: { ...blank(), ...definitionOf(c) } as Draft, editing: c })
                  }
                >
                  Edit
                </Button>
              ) : null}
              <Button
                onClick={() =>
                  saveFile(
                    `${c.label.replace(/[^\w-]+/g, '-')}.connector.json`,
                    JSON.stringify(definitionOf(c), null, 2),
                  )
                }
              >
                Export JSON
              </Button>
              {c.own ? (
                <Button color="error" onClick={() => setRemoving(c)}>
                  Delete
                </Button>
              ) : null}
            </Paper>
          ))}
        </Box>
      )}
      {editor ? (
        <ConnectorDialog
          start={editor.start}
          editing={editor.editing}
          onClose={() => setEditor(null)}
        />
      ) : null}
      <ConfirmDialog
        open={!!removing}
        title="Delete carrier connector"
        message={`Delete ${removing?.label ?? ''}? A carrier connected through it must be changed first.`}
        confirmLabel="Delete"
        danger
        busy={remove.isPending}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing, {
            onSuccess: () => {
              notify.success(`${removing.label} deleted.`);
              setRemoving(null);
            },
            onError: (err) => notify.error(err),
          })
        }
      />
    </Box>
  );
}
